import React from "react";

import { faEye, faPlus } from "@fortawesome/pro-regular-svg-icons";

import { PatchViewerModal } from "src/adminPanel/modulesV2/PatchViewerModal";
import {
  getOperationStatusPillVariant,
  truncateJson,
} from "src/adminPanel/modulesV2/utils";
import { Module, ModuleOperation } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Pill } from "src/base-components/Pill";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { useModal } from "src/design-system/Modal";

type Props = {
  module: Module;
  onCreateOperation?: () => void;
};

const OperationRow: React.FC<{
  operation: ModuleOperation;
  onView: (operation: ModuleOperation) => void;
}> = ({ operation, onView }) => {
  return (
    <tr className="transition-colors hover:bg-gray-50">
      <td className="whitespace-nowrap px-4 py-2">
        <TruncatedIdPill id={operation.id} feedback="inline" />
      </td>
      <td className="whitespace-nowrap px-4 py-2">
        <Pill variant={getOperationStatusPillVariant(operation.status)}>
          <Pill.Text fontType="text">{operation.status}</Pill.Text>
        </Pill>
      </td>
      <td className="max-w-xs px-4 py-2">
        <button
          className="block w-full cursor-pointer truncate text-left font-code-12 text-gray-600 hover:text-gray-900"
          type="button"
          onClick={() => onView(operation)}
        >
          {truncateJson(operation.patch)}
        </button>
      </td>
      <td className="whitespace-nowrap px-4 py-2">
        <EllipsisOptionsDropdown
          dataLoc="operation-actions"
          elements={[
            {
              key: "View patch",
              icon: faEye,
              action: () => onView(operation),
            },
          ]}
          iconSize="xs"
        />
      </td>
    </tr>
  );
};

export const ModuleOperationsDetail: React.FC<Props> = ({
  module,
  onCreateOperation,
}) => {
  const {
    isOpen,
    openModal,
    closeModal,
    afterLeave,
    data: selectedOperation,
  } = useModal<ModuleOperation>();

  return (
    <div className="space-y-4 p-4">
      {/* Active Operations */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-gray-200 border-b px-4 py-3">
          <h4 className="font-inter-semibold-13px text-gray-900">
            Active Operations ({module.activeOperations.length})
          </h4>
          {onCreateOperation && (
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={onCreateOperation}
            >
              New Operation
            </Button>
          )}
        </div>
        {module.activeOperations.length === 0 ? (
          <div className="px-4 py-6 text-center font-inter-normal-13px text-gray-500">
            No active operations
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    Patch
                  </th>
                  <th className="w-10 px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {module.activeOperations.map((op) => (
                  <OperationRow key={op.id} operation={op} onView={openModal} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Latest Resolved Operation */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-gray-200 border-b px-4 py-3">
          <h4 className="font-inter-semibold-13px text-gray-900">
            Latest Resolved Operation
          </h4>
        </div>
        {module.latestResolvedOperation ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left font-inter-semibold-12px text-gray-500 uppercase tracking-wider">
                    Patch
                  </th>
                  <th className="w-10 px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <OperationRow
                  operation={module.latestResolvedOperation}
                  onView={openModal}
                />
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-6 text-center font-inter-normal-13px text-gray-500">
            None recorded
          </div>
        )}
      </div>

      <PatchViewerModal
        afterLeave={afterLeave}
        isOpen={isOpen}
        operation={selectedOperation}
        onClose={closeModal}
      />
    </div>
  );
};
